import { useEffect, useRef, useState } from 'react';
import { Alert, List, NumberInput, SegmentedControl, Select, Stack, Tabs, Text, useMantineColorScheme, type MantineColorScheme } from '@mantine/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { notifications } from '@mantine/notifications';
import type { FieldIssue } from '@flow/core';
import { useDeps, useUseCases } from '../../app/services';
import { downloadJson, storageStatus } from '../../infra/browser';
import { PageShell } from '../../ui/PageShell';
import { ActionButton, Part, PART_GAP, Row } from '../../ui/SettingsParts';
import { K } from '../../ui/Kbd';
import { TimerAlertsEditor } from '../timer/TimerSound';
import { AccountCard } from '../account/AccountCard';
import { TabroomCard } from '../tabroom/TabroomCard';
import { SyncCard } from '../sync/SyncCard';
import { UI_FONTS, WRITING_FONTS, type FontChoice } from '../../ui/fonts';
import { paths, useQueryState, type SettingsSection } from '../../app/routes';
import { useIsOwner } from '../tabroom/useTabroom';
import { Brand, Copy } from '../../ui/Brand';

const mb = (n: number | null) => (n === null ? '?' : `${(n / 1024 / 1024).toFixed(1)} MB`);

/** The tabs. The old per-section addresses (privacy, backup, storage) open the Data tab, scrolled to that part. */
type Tab = 'account' | 'display' | 'timer' | 'data' | 'tabroom';
const TAB_OF: Record<SettingsSection, Tab> = { account: 'account', tabroom: 'tabroom', display: 'display', timer: 'timer', data: 'data', privacy: 'data', backup: 'data', storage: 'data' };
const TABS: { value: Tab; label: string; lead: string }[] = [
  { value: 'account', label: 'Account', lead: "Who you're signed in as, and sync between your devices." },
  { value: 'display', label: 'Display', lead: 'How flow looks. Fonts and the flow layout follow your account to every device; text size is set per device.' },
  { value: 'timer', label: 'Timer', lead: 'Sounds and signals while a speech runs.' },
  { value: 'data', label: 'Data', lead: 'What stays on this device, what leaves it, and how to keep a copy.' },
  { value: 'tabroom', label: 'Tabroom', lead: '{flow} watches your own Tabroom account for assignments and messages. Personal use only.' },
];

/** A dropdown where each option is shown in its own font. */
const FontPicker = <T extends string>({ fonts, value, onChange, testId }: { fonts: Record<T, FontChoice>; value: T; onChange: (v: T) => void; testId: string }) => (
  <Select
    size="xs"
    w={210}
    value={value}
    onChange={(v) => v && onChange(v as T)}
    data={(Object.keys(fonts) as T[]).map((k) => ({ value: k, label: fonts[k].label }))}
    renderOption={({ option }) => <span style={{ fontFamily: fonts[option.value as T].family }}>{option.label}</span>}
    allowDeselect={false}
    comboboxProps={{ zIndex: 1000 }}
    data-testid={testId}
  />
);

/** /settings/:section. Each tab is a page of its own, with a line on what the group is for. */
export const SettingsPage = ({ section }: { section: SettingsSection }) => {
  const owner = useIsOwner();
  const deps = useDeps();
  const uc = useUseCases();
  const url = useQueryState();
  const settings = useLiveQuery(() => deps.settings.get(), []);
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const [storage, setStorage] = useState<{ persisted: boolean | null; usage: number | null; quota: number | null } | null>(null);
  const [importIssues, setImportIssues] = useState<FieldIssue[] | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const tab = TAB_OF[section];

  useEffect(() => {
    void storageStatus().then(setStorage);
  }, []);
  // An old address like /settings/backup: the Data tab, scrolled to Backup.
  const ready = !!settings;
  useEffect(() => {
    if (ready && section !== tab) document.getElementById(`settings-${section}`)?.scrollIntoView({ block: 'start' });
  }, [ready, section, tab]);

  if (!settings) return null;
  const tabs = TABS.filter((t) => t.value !== 'tabroom' || owner);
  const lead = TABS.find((t) => t.value === tab)!.lead;

  return (
    <PageShell title="Settings" crumbs={[{ label: 'Tournaments', href: paths.dashboard }]}>
      <Tabs value={tab} onChange={(v) => v && v !== tab && url.navigate(paths.settings(v as Tab))} keepMounted={false}>
        <Tabs.List mb="lg">
          {tabs.map((t) => <Tabs.Tab key={t.value} value={t.value} data-testid={`settings-tab-${t.value}`}>{t.label}</Tabs.Tab>)}
        </Tabs.List>
        <Text size="sm" c="dimmed" mb="md" data-testid="settings-lead"><Copy>{lead}</Copy></Text>

        <Tabs.Panel value="account">
          <Stack gap={PART_GAP}>
            <AccountCard />
            <SyncCard />
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="display">
          <Stack gap="xl">
          <Row label="Theme" help="Auto follows your device's light or dark setting. Set per device.">
            <SegmentedControl size="xs" value={colorScheme} onChange={(v) => setColorScheme(v as MantineColorScheme)} data={[{ value: 'auto', label: 'Auto' }, { value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }]} />
          </Row>
          <Row label="Interface font" help={UI_FONTS[settings.uiFont].note}>
            <FontPicker fonts={UI_FONTS} value={settings.uiFont} onChange={(v) => void deps.settings.put({ ...settings, uiFont: v })} testId="ui-font" />
          </Row>
          <Row label="Writing font" help={<>The flow, notes and ballot. {WRITING_FONTS[settings.writingFont].note ?? 'Any font works here; a monospace takes more room.'}</>}>
            <FontPicker fonts={WRITING_FONTS} value={settings.writingFont} onChange={(v) => void deps.settings.put({ ...settings, writingFont: v })} testId="writing-font" />
          </Row>
          <Row label="Flow layout" help="One sheet: every speech in order (C1, C2, CF1, R1…). Per case: a sheet for each team's case, the way paper flows are kept.">
            <SegmentedControl
              size="xs"
              value={settings.flowLayout}
              onChange={(v) => void deps.settings.put({ ...settings, flowLayout: v as typeof settings.flowLayout })}
              data={[{ value: 'single', label: 'One sheet' }, { value: 'perCase', label: 'Per case' }]}
              data-testid="flow-layout"
            />
          </Row>
          <Row label="Flow text size" help={<>Set per device (an iPad and a laptop want different sizes). <K>⌥=</K> and <K>⌥−</K> change it mid-round.</>}>
            <NumberInput size="xs" w={100} min={0.7} max={1.8} step={0.1} decimalScale={1} value={settings.flowFontScale} onChange={(v) => typeof v === 'number' && v >= 0.7 && v <= 1.8 && void deps.settings.put({ ...settings, flowFontScale: v })} />
          </Row>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="timer">
          <Stack gap="xl">
            <TimerAlertsEditor spacing="xl" />
            <Row label="What happens either way" help={<>The clock flashes at 30s, 10s and time's up, beeps or not. Beeps play even with the device on silent, but on iPad they can't play while <Brand /> is in the background. The speaker icon in the timer bar has the same controls.</>} />
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="data">
          <Stack gap={PART_GAP}>
          <Part title="Privacy" id="settings-privacy">
          <Row label="Debater names stay on this device" help="They're never uploaded, and never synced to your other devices. Team codes, flows and ballots are kept without names, for your own reference and calibration. Delete names this many hours after a round is marked submitted:">
            <NumberInput size="xs" w={90} min={0} max={720} value={settings.purgeNamesAfterHours} onChange={(v) => typeof v === 'number' && v >= 0 && void deps.settings.put({ ...settings, purgeNamesAfterHours: Math.round(v) })} />
          </Row>
          </Part>

          <Part
            title="Backup"
            id="settings-backup"
            lead="Sync keeps your devices in step; a backup file is for the day something goes wrong with all of them. Export one at the end of each tournament day. Backups include debater names, so keep them private."
            actions={
              <>
              <ActionButton
                primary
                onClick={async () => {
                  const b = await uc.exportBackup();
                  downloadJson(`flow-backup-${new Date().toISOString().slice(0, 10)}.json`, b);
                }}
              >
                Export backup
              </ActionButton>
              <ActionButton onClick={() => fileRef.current?.click()}>Import backup…</ActionButton>
              <input
                ref={fileRef}
                type="file"
                accept="application/json,.json"
                hidden
                onChange={async (e) => {
                  const file = e.currentTarget.files?.[0];
                  e.currentTarget.value = '';
                  if (!file) return;
                  let json: unknown;
                  try {
                    json = JSON.parse(await file.text());
                  } catch {
                    setImportIssues([{ path: '', code: 'json', message: "That file isn't valid JSON. Is it a {flow} backup?" }]);
                    return;
                  }
                  const r = await uc.importBackup(json);
                  if (r.ok) {
                    setImportIssues(null);
                    notifications.show({ color: 'green', message: `Imported ${r.value.rounds.length} rounds.` });
                  } else setImportIssues(r.error.kind === 'validation' ? r.error.issues : [{ path: '', code: r.error.kind, message: r.error.message }]);
                }}
              />
              </>
            }
          >
          {importIssues && (
            <Alert color="red" title="Nothing was imported. The file has problems:">
              <List size="sm">
                {importIssues.slice(0, 20).map((i, k) => (
                  <List.Item key={k}>{i.path ? <code>{i.path}</code> : null} <Copy>{i.message}</Copy></List.Item>
                ))}
              </List>
              {importIssues.length > 20 && <Text size="xs">…and {importIssues.length - 20} more.</Text>}
            </Alert>
          )}

          </Part>

          <Part title="Storage" id="settings-storage">
          <Row
            label={<>Protected from automatic cleanup: {storage?.persisted === null ? 'unknown' : storage?.persisted ? 'yes' : 'no'} · using {mb(storage?.usage ?? null)} of {mb(storage?.quota ?? null)}</>}
            help={<>Browsers can clear a site's data when space runs low. <Brand /> asks to be protected; on iPad, install it to the Home Screen so iOS doesn't clear it.</>}
          />
          </Part>
          </Stack>
        </Tabs.Panel>

        {owner && (
          <Tabs.Panel value="tabroom">
            <TabroomCard />
          </Tabs.Panel>
        )}
      </Tabs>
    </PageShell>
  );
};
